import React, { useEffect, useState, useMemo } from "react";
import {
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  SimpleGrid,
  Separator,
} from "@chakra-ui/react";
import { useLanguage } from "@/contexts/languageContext";
import { useApi } from "@/uses/useApi";
import Graph from "components/logs/Graph";
import { Users, LogIn, AlertTriangle, Activity } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, color = "#8884d8" }) => (
  <Box
    bg="white"
    borderRadius="xl"
    p={6}
    shadow="sm"
    border="1px solid"
    borderColor="gray.100"
  >
    <HStack gap={4}>
      <Box bg={`${color}20`} p={3} borderRadius="lg">
        <Icon size={24} color={color} />
      </Box>
      <VStack align="start" gap={0}>
        <Text fontSize="sm" color="gray.500" fontWeight="medium">
          {label}
        </Text>
        <Text fontSize="2xl" fontWeight="bold">
          {value}
        </Text>
      </VStack>
    </HStack>
  </Box>
);

export const StatsList = () => {
  const { t } = useLanguage();
  const api = useApi();
  const [loginLogs, setLoginLogs] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    api.ApiService.get("log", {
      where: { message: "LOGIN", type: "INFO" },
      order: [["createdAt", "ASC"]],
    })
      .then((res) => {
        if (res?.data?.type === "success") {
          setLoginLogs(res?.data?.value);
        }
      })
      .catch((e) => console.log(e?.response?.data));

    api.ApiService.get("log", {
      where: { type: "ERROR" },
      order: [["createdAt", "ASC"]],
    })
      .then((res) => {
        if (res?.data?.type === "success") {
          setErrorLogs(res?.data?.value);
        }
      })
      .catch((e) => console.log(e?.response?.data));

    api.ApiService.get("count_user")
      .then((res) => {
        if (res?.data?.type === "success") {
          setTotalUsers(res?.data?.value?.count ?? 0);
        }
      })
      .catch((e) => console.log(e?.response?.data));
  }, []);
  const todayLogins = useMemo(() => {
    const today = new Date().toISOString().split("T")[0];
    return loginLogs.filter((log) => log.createdAt.startsWith(today)).length;
  }, [loginLogs]);

  return (
    <VStack
      position="relative"
      w="100%"
      overflow="auto"
      flex={1}
      p={6}
      gap={6}
      align="stretch"
    >
      <Heading size="lg">{t("stats.title")}</Heading>
      <SimpleGrid columns={{ base: 1, lg: 3 }} gap={4}>
        <StatCard
          icon={Activity}
          label={t("stats.todayLogins")}
          value={todayLogins}
          color="#8884d8"
        />
        <StatCard
          icon={LogIn}
          label={t("stats.totalLogins")}
          value={loginLogs.length}
          color="#38A169"
        />
        <StatCard
          icon={Users}
          label={t("stats.users")}
          value={totalUsers}
          color="#3182CE"
        />
      </SimpleGrid>

      <Separator />

      <Graph logs={loginLogs} />

      {errorLogs.length > 1 && (
        <Box
          bg="white"
          borderRadius="xl"
          p={6}
          shadow="sm"
          border="1px solid"
          borderColor="gray.100"
        >
          <Text fontSize="md" fontWeight="semibold" mb={4}>
            {t("stats.errors")}
          </Text>
          <Graph logs={errorLogs} />
        </Box>
      )}
    </VStack>
  );
};

export default StatsList;
